import { crossesBoundary } from '../building.js';
import type { GameState, Passenger } from '../types.js';
import { callOutArrived } from './emergency.js';
import { capacityOf } from './powers.js';
import { creditDelivery } from './service.js';

/**
 * Where this rider actually gets out.
 *
 * Normally their destination. In a building with an interchange, a journey that
 * crosses the boundary is two journeys: the first leg ends at the transfer
 * floor, and only the second one delivers them. This is the whole reason an
 * interchange building plays differently — "take them to floor 11" stops being
 * one decision and becomes two, with a queue building between them.
 */
export function alightFloor(state: GameState, passenger: Passenger): number {
  const ic = state.building.interchange;
  if (!ic || passenger.transferred) return passenger.destination;
  return crossesBoundary(state.building, passenger.standingFloor, passenger.destination)
    ? ic.floor
    : passenger.destination;
}

/**
 * Where the car is worth taking this rider next: the floors left on their
 * round (ADR-0031), or, once there are none, where they get out. The bots and
 * the hints ask this; a rider owes nothing at their destination until then.
 */
export function nextStops(state: GameState, passenger: Passenger): number[] {
  return passenger.errands.length > 0 ? [...passenger.errands] : [alightFloor(state, passenger)];
}

/** Every floor this rider still needs the car to open at: their round, then where they get out. */
export function stopsAhead(state: GameState, passenger: Passenger): number[] {
  const out = alightFloor(state, passenger);
  return [...passenger.errands.filter((floor) => floor !== out), out];
}

/**
 * Passengers move one at a time, each consuming a `transferMs` slot. That time
 * cost is what turns "who do I take?" into a real trade-off rather than a free
 * action — see the roadmap's core hypothesis.
 */

function alightCandidate(state: GameState, floor: number): Passenger | undefined {
  for (const id of state.elevator.riders) {
    const p = state.passengers.find((x) => x.id === id);
    // Not while there is still a round to make (ADR-0031).
    if (p && p.errands.length === 0 && alightFloor(state, p) === floor) return p;
  }
  return undefined;
}

/** A rider with a stop on their round here: the mail goes out through this door. */
function errandCandidate(state: GameState, floor: number): Passenger | undefined {
  for (const id of state.elevator.riders) {
    const p = state.passengers.find((x) => x.id === id);
    if (p?.errands.includes(floor)) return p;
  }
  return undefined;
}

/** Standing on this floor, and allowed through the door that is open now. */
function availableHere(state: GameState, p: Passenger, floor: number): boolean {
  return (
    p.state === 'waiting' &&
    p.standingFloor === floor &&
    state.score.doorCycles >= p.boardableFromCycle
  );
}

/** Everyone from this group still standing on this floor. */
function waitingGroupMembers(state: GameState, groupId: string, floor: number): Passenger[] {
  return state.passengers.filter((p) => p.groupId === groupId && availableHere(state, p, floor));
}

/**
 * Whether this passenger can board — and, if they travel as a group, whether the
 * whole group can.
 *
 * Groups are all-or-nothing: boarding half a pair and leaving the other half on
 * the floor is exactly the outcome the trait exists to forbid. Because the check
 * covers the entire remaining group before the first member steps in, the rest
 * are guaranteed to fit behind them.
 */
export function canBoard(state: GameState, passenger: Passenger, floor: number): boolean {
  const { elevator } = state;
  // An arrest rides alone (ADR-0029): into an empty cabin, and nobody after it.
  if (passenger.alone && elevator.riders.length > 0) return false;
  if (elevator.riders.some((id) => state.passengers.find((p) => p.id === id)?.alone)) return false;
  if (passenger.groupId === null) return elevator.load + passenger.size <= capacityOf(state);

  const remaining = waitingGroupMembers(state, passenger.groupId, floor);
  const needed = remaining.reduce((sum, p) => sum + p.size, 0);
  return elevator.load + needed <= capacityOf(state);
}

function boardCandidate(state: GameState, floor: number): Passenger | undefined {
  const { elevator } = state;

  // Finish any group that is already half aboard before starting anyone new,
  // so a third party cannot slip into the seat their companion needs.
  const partial = state.passengers.find(
    (p) =>
      availableHere(state, p, floor) &&
      p.groupId !== null &&
      state.passengers.some((other) => other.groupId === p.groupId && other.state === 'riding'),
  );
  if (partial) return partial;

  if (state.mode === 'select') {
    for (const id of elevator.boardQueue) {
      const p = state.passengers.find((x) => x.id === id);
      if (p && availableHere(state, p, floor) && canBoard(state, p, floor)) return p;
    }
    return undefined;
  }

  // dispatch: anyone the player marked to go first (ADR-0025), latest first —
  // then whoever has been waiting longest. Either way only someone who fits:
  // a mark is a preference, never a reason to hold the door.
  for (const id of elevator.priority) {
    const p = state.passengers.find((x) => x.id === id);
    if (p && availableHere(state, p, floor) && canBoard(state, p, floor)) return p;
  }
  // An emergency goes before the queue (ADR-0029) — if it fits.
  const urgent = firstToArrive(state, floor, (p) => p.critical);
  if (urgent) return urgent;
  return firstToArrive(state, floor, () => true);
}

/**
 * Whoever has waited here longest, of those who may get on now and fit. A level
 * lists its own people (a postman, a train's commuters, a crew) after its crowd,
 * so the list is not the order people came in: going by it put someone with a
 * clock behind people who arrived after them (the owner, PLAYTEST_LOG
 * 2026-10-29). Ties go by the list.
 */
function firstToArrive(
  state: GameState,
  floor: number,
  also: (p: Passenger) => boolean,
): Passenger | undefined {
  let first: Passenger | undefined;
  for (const p of state.passengers) {
    if (!also(p) || !availableHere(state, p, floor) || !canBoard(state, p, floor)) continue;
    if (first === undefined || p.waitingSinceMs < first.waitingSinceMs) first = p;
  }
  return first;
}

/** The people waiting at a floor, in the order the lift takes them: whoever came first. */
export function inArrivalOrder(waiting: readonly Passenger[]): Passenger[] {
  return [...waiting].sort((a, b) => a.waitingSinceMs - b.waitingSinceMs);
}

/** True when nobody can step in or out right now, so the doors have no reason to stay open. */
export function transfersExhausted(state: GameState, floor: number): boolean {
  return (
    alightCandidate(state, floor) === undefined &&
    errandCandidate(state, floor) === undefined &&
    boardCandidate(state, floor) === undefined
  );
}

/** Perform at most one transfer. Alighting always wins — you cannot fill a seat before it empties. */
export function performOneTransfer(state: GameState, floor: number): boolean {
  const { elevator, score } = state;

  const leaving = alightCandidate(state, floor);
  if (leaving) {
    elevator.riders.splice(elevator.riders.indexOf(leaving.id), 1);
    elevator.load -= leaving.size;
    score.totalRideMs += state.elapsedMs - (leaving.boardedMs ?? state.elapsedMs);

    // Changing lifts, not arriving. They step out and wait again, with a fresh
    // patience clock — being carried halfway is service, and punishing them for
    // it would make the interchange feel like a trap rather than a structure.
    if (leaving.destination !== floor) {
      leaving.state = 'waiting';
      leaving.standingFloor = floor;
      leaving.waitingSinceMs = state.elapsedMs;
      leaving.transferred = true;
      leaving.boardedMs = null;
      // Not through this door. The car has to come back for them.
      leaving.boardableFromCycle = state.score.doorCycles + 1;
      score.transfers += 1;
      return true;
    }

    leaving.state = 'delivered';
    leaving.deliveredMs = state.elapsedMs;
    score.delivered += 1;
    creditDelivery(state, leaving);
    // A crew at its incident: their return starts counting down (ADR-0029).
    if (leaving.callout) callOutArrived(state, leaving);
    return true;
  }

  // A stop on a round (ADR-0031): one transfer slot to hand it over, and it is
  // crossed off. After anyone getting out, before anyone getting in.
  const round = errandCandidate(state, floor);
  if (round) {
    round.errands.splice(round.errands.indexOf(floor), 1);
    return true;
  }

  const entering = boardCandidate(state, floor);
  if (entering) {
    elevator.riders.push(entering.id);
    elevator.load += entering.size;
    entering.state = 'riding';
    entering.boardedMs = state.elapsedMs;
    score.totalWaitMs += state.elapsedMs - entering.waitingSinceMs;
    const queued = elevator.boardQueue.indexOf(entering.id);
    if (queued !== -1) elevator.boardQueue.splice(queued, 1);
    const marked = elevator.priority.indexOf(entering.id);
    if (marked !== -1) elevator.priority.splice(marked, 1);
    return true;
  }

  return false;
}

/**
 * Whether the cabin can take everyone standing here right now. When it cannot,
 * who goes first is a real choice (ADR-0025) — the moment the game should let
 * the player make it, and the only moment a mark changes anything.
 */
export function cannotTakeAll(state: GameState, floor: number): boolean {
  const free = capacityOf(state) - state.elevator.load;
  let wanting = 0;
  for (const p of state.passengers) if (availableHere(state, p, floor)) wanting += p.size;
  return wanting > free;
}

/** Group-aware: tapping one member queues the whole group, since they board together. */
export function queueableWith(state: GameState, passenger: Passenger): Passenger[] {
  if (passenger.groupId === null) return [passenger];
  return waitingGroupMembers(state, passenger.groupId, passenger.standingFloor);
}
